import Box from '~/common/components/Box';
import Flex from '~/common/components/Flex';
import RadioComponent from '~/common/components/Radios/Radio';
import RadioGroup from '~/common/components/Radios/RadioGroup';
import Text from '~/common/components/Texts/Text';

import ProcessedText from '../../components/utilityComponents/ProcessedText';
import StandardFormControl from '../../components/utilityComponents/StandardFormControl';
import InputController from '../../controllers/InputController';
import { useQuestionLibraryActions } from '../../store/questionLibraryStore';
import type { SmartAnswer } from '../../types/smartAnswers';
import type { QuestionLibraryError } from '../../utils/errorHandling';

type SimpleRadioWithInputOptionProps = {
  field?: any;
  name?: string;
  shouldDisableInput?: boolean;
  label?: string;
  rules?: any;
  options?: any[];
  infoToolTip?: string;
  error?: QuestionLibraryError;
  smartAnswer?: SmartAnswer;
  isLeftAligned?: boolean;
};

// Left-aligned values: top padding is 5px tighter than the default 1rem, the 0.25rem left
// padding lines the radios up with SimpleRadio, and the value row sits 5px closer and 3px further right.
// Inputs are pulled 10px left so the input box starts just left of its radio.
const getOptionLayout = (isLeftAligned: boolean) => {
  if (isLeftAligned) {
    return {
      padding: '0.6875rem 2.1875rem 1rem 0.25rem',
      justifyContent: 'flex-start',
      valueMarginTop: '-0.3125rem',
      valuePaddingLeft: '0.1875rem',
      inputMarginLeft: '-0.625rem',
    };
  }
  return {
    padding: '1rem',
    justifyContent: 'flex-end',
    valueMarginTop: '0',
    valuePaddingLeft: '0',
    inputMarginLeft: '0',
  };
};

const getValueMarginLeft = (
  option: { input?: unknown },
  optionLayout: ReturnType<typeof getOptionLayout>,
) => {
  if (option.input) {
    return optionLayout.inputMarginLeft;
  }
  return '0';
};

export default function SimpleRadioWithInputOption({
  field,
  error,
  isLeftAligned = false,
  ...question
}: Readonly<SimpleRadioWithInputOptionProps>) {
  const { name, options, shouldDisableInput = true } = question;
  const { setFocusedQuestion } = useQuestionLibraryActions();
  const optionLayout = getOptionLayout(isLeftAligned);

  return (
    <StandardFormControl
      field={field}
      error={error}
      padding={'0, 0.25rem, 1rem, 0.25rem'}
      {...question}
      onFocus={() => setFocusedQuestion(question.name, question.smartAnswer)}
    >
      <RadioGroup display={'flex'} {...field}>
        {options.map((option) => (
          <Box
            key={option.value}
            padding={optionLayout.padding}
            data-testid={`${name}-${option.value}-option`}
          >
            <Flex>
              <RadioComponent
                key={option.value}
                name={name}
                value={option.value}
                isInvalid={error.hasError}
                marginRight={'1rem'}
                data-testid={`${name}-${option.value}`}
              >
                <Text>{option.label}</Text>
              </RadioComponent>
            </Flex>
            <Flex
              align={'center'}
              justifyContent={optionLayout.justifyContent}
              marginTop={optionLayout.valueMarginTop}
              marginLeft={getValueMarginLeft(option, optionLayout)}
              paddingLeft={optionLayout.valuePaddingLeft}
              height={'100%'}
              width={'100%'}
              data-testid={`${name}-${option.value}-value`}
            >
              {option.additionalDisplayText && (
                <ProcessedText
                  text={option.additionalDisplayText}
                  placeholders={option.placeholders}
                />
              )}
              {option.input && (
                <InputController
                  question={option.input}
                  disabled={shouldDisableInput && option.value !== field.value}
                  isRequirable={field.value === option.value}
                />
              )}
            </Flex>
          </Box>
        ))}
      </RadioGroup>
    </StandardFormControl>
  );
}
